/**
 * Soniox streaming STT over WebSocket — token partials, semantic `<end>` endpointing,
 * auto-reconnect (re-minting auth — temp tokens expire). Portable: the microphone is an
 * injected AudioSource, not spawned here.
 *
 * Browser auth: NEVER ship the real key — your backend mints a short-lived key via
 * `POST https://api.soniox.com/v1/auth/temporary-api-key` (sent with the real key) and the
 * client passes `auth: () => fetch('/your-be/soniox-token').then(r => r.text())`.
 */
import { forComponent } from '../logging';
import { type AudioSource, type AuthProvider, resolveAuth, STT_SAMPLE_RATE } from './types';

const log = forComponent('SonioxSTT');
const now = () => performance.now();

export class SonioxSTTOptions {
  auth: AuthProvider = '';
  source!: AudioSource;
  model = 'stt-rt-preview';
  languageHints = ['en'];
  /** Client-side endpoint: finalized text + no new tokens for this long = utterance (don't wait for
   *  Soniox's semantic <end>, which adds 0.5-1.5s — the difference between ping-pong and lag). */
  silenceEndpointMs = 500;
  /** No-audio watchdog: if the mic source stops delivering chunks for this long, capture is dead →
   *  fire onFatal + stop (else Soniox idle-timeouts and reconnect-loops forever). 0 = disable. */
  noAudioTimeoutMs = 10_000;
}

export class SonioxSTT {
  public options: SonioxSTTOptions;
  private ws!: WebSocket;
  private stopped = false;
  private sourceStarted = false;
  public onPartial: (text: string) => void = () => {};
  public onUtterance: (text: string, endpointAt: number) => void = () => {};
  /** mic energy (RMS) per chunk — drives the energy-based heuristic barge-in tier */
  public onLevel: (rms: number) => void = () => {};
  /** Unrecoverable: the mic source stopped delivering audio (Soniox starves → idle-timeout reconnect
   *  loop). The host tears voice down instead of spinning forever. */
  public onFatal: (message: string) => void = () => {};
  /** Diagnostic tap (provider errors, ws reconnects, no-audio watchdog). Fire-and-forget: a throwing
   *  handler disables itself — never affects transcription. Wired by VoiceIO / the lab bridge. */
  public onDiag: (ev: { t: number; kind: string; [k: string]: unknown }) => void = () => {};
  private diagOn = true;
  private diag(kind: string, fields?: Record<string, unknown>): void {
    if (!this.diagOn) return;
    try { this.onDiag({ t: now(), kind, ...fields }); }
    catch (e) { this.diagOn = false; log.debug(`onDiag threw — STT diagnostics disabled: ${e instanceof Error ? e.message : e}`); }
  }
  private lastChunkAt = 0;       // timestamp of the most recent mic chunk (0 = none yet)
  private startedChunksAt = 0;   // when capture started (grace before the first chunk)
  private noAudioTimer: ReturnType<typeof setInterval> | null = null;
  private finalText = '';
  private partialText = '';
  private lastChangeAt = 0;
  private lastCombined = '';
  private endpointTimer: ReturnType<typeof setInterval> | null = null;
  private firstTokenAt = 0; // first speech token in current utterance

  constructor(options?: Partial<SonioxSTTOptions>) {
    this.options = { ...new SonioxSTTOptions(), ...options };
  }

  get usingAec(): boolean {
    return this.options.source?.aec ?? false;
  }

  private async connectWs(): Promise<void> {
    const apiKey = await resolveAuth(this.options.auth); // per-connect: temp tokens expire
    this.ws = new WebSocket('wss://stt-rt.soniox.com/transcribe-websocket');
    await new Promise<void>((res, rej) => {
      this.ws.onopen = () => res();
      this.ws.onerror = (e: any) => rej(new Error(`soniox ws: ${e.message || 'connect failed'}`));
    });
    this.ws.send(
      JSON.stringify({
        api_key: apiKey,
        model: this.options.model,
        audio_format: 'pcm_s16le',
        sample_rate: STT_SAMPLE_RATE,
        num_channels: 1,
        language_hints: this.options.languageHints,
        enable_endpoint_detection: true,
      }),
    );
    this.ws.onmessage = (ev) => this.handle(JSON.parse(String(ev.data)));
    this.ws.onclose = (ev: any) => {
      if (this.stopped) return;
      log.warn(`soniox ws closed (${ev.code} ${ev.reason || ''}) — reconnecting`);
      this.diag('stt_ws_closed', { code: ev.code, reason: String(ev.reason || ''), reconnecting: true });
      this.reset();
      this.connectWs().catch((e) => { log.error(`soniox reconnect failed: ${e.message}`); this.diag('stt_reconnect_failed', { message: e.message }); });
    };
  }

  async start(): Promise<void> {
    await this.connectWs();
    if (this.sourceStarted) return; // reconnect path — source keeps running
    this.sourceStarted = true;
    // client-side stability endpoint (faster than Soniox's semantic <end> AND its finalization lag:
    // finals trail partials by ~1s — text that has stopped CHANGING is the utterance)
    this.endpointTimer = setInterval(() => {
      const combined = (this.finalText + this.partialText).trim();
      if (!combined || now() - this.lastChangeAt < this.options.silenceEndpointMs) return;
      if (this.firstTokenAt) log.debug(`stt: ${Math.round(now() - this.firstTokenAt)}ms first-token→silence-endpoint, "${combined.slice(0, 60)}"`);
      this.reset();
      this.onUtterance(combined, now());
    }, 120);
    (this.endpointTimer as any).unref?.();
    // No-audio watchdog (SLIDING window, not cumulative): healthy capture delivers a chunk ~every 100ms
    // (the AEC helper emits even during silence). If chunks STOP for 10s, capture died — e.g. the
    // CoreAudio input tap stopped firing after a TTS turn — and Soniox would idle-timeout + reconnect
    // forever. A cumulative-from-start check would miss this (the greeting already sent bytes). Tear
    // down with a clear message. The grace (startedChunksAt) covers a slow first chunk.
    this.startedChunksAt = now();
    const noAudioMs = this.options.noAudioTimeoutMs;
    if (noAudioMs > 0) {
      this.noAudioTimer = setInterval(() => {
        if (this.stopped) return;
        const ref = this.lastChunkAt || this.startedChunksAt;
        if (now() - ref > noAudioMs) {
          log.error(`stt: no mic audio for >${Math.round(noAudioMs / 1000)}s — capture device stopped delivering`);
          this.diag('stt_watchdog_fatal', { noAudioMs });
          this.onFatal('microphone stopped delivering audio (try a different input device, e.g. AirPods, or check System Settings → Sound → Input)');
          this.stop();
        }
      }, Math.max(250, Math.min(2000, noAudioMs / 4)));
      (this.noAudioTimer as any).unref?.();
    }
    await this.options.source.start((chunk) => {
      this.lastChunkAt = now();
      let sum = 0;
      const view = new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength);
      for (let i = 0; i + 1 < chunk.byteLength; i += 2) { const v = view.getInt16(i, true); sum += v * v; }
      this.onLevel(Math.sqrt(sum / (chunk.byteLength / 2)));
      if (this.ws.readyState === WebSocket.OPEN) this.ws.send(chunk);
    });
  }
  private handle(m: any): void {
    if (m.error_message) { this.diag('stt_error', { message: String(m.error_message), code: m.error_code }); return log.error(`soniox: ${m.error_message}`); }
    let endpoint = false;
    for (const t of m.tokens ?? []) {
      if (t.text === '<end>') endpoint = true;
      else if (t.is_final) this.finalText += t.text;
    }
    this.partialText = (m.tokens ?? []).filter((t: any) => !t.is_final && t.text !== '<end>').map((t: any) => t.text).join('');
    const combined = this.finalText + this.partialText;
    if (combined !== this.lastCombined) {
      this.lastCombined = combined; this.lastChangeAt = now();
      if (!this.firstTokenAt && combined.trim()) this.firstTokenAt = now();
    }
    this.onPartial(combined);
    if (endpoint && this.finalText.trim()) {
      const utterance = this.finalText.trim();
      if (this.firstTokenAt) log.debug(`stt: ${Math.round(now() - this.firstTokenAt)}ms first-token→endpoint, "${utterance.slice(0, 60)}"`);
      this.reset();
      this.onUtterance(utterance, now());
    }
  }
  reset(): void {
    this.finalText = '';
    this.partialText = '';
    this.lastCombined = '';
    this.firstTokenAt = 0;
  }
  stop(): void {
    this.stopped = true;
    if (this.endpointTimer) clearInterval(this.endpointTimer);
    if (this.noAudioTimer) clearInterval(this.noAudioTimer);
    this.options.source?.stop();
    if (this.ws) this.ws.onclose = null as any;
    this.ws?.close();
  }
}
